import { useEffect, useMemo, useState } from "react";
import { Download, FileSpreadsheet, Info } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";
import FilterPills from "./admin/FilterPills";
import ReportsNightly from "./ReportsNightly";
import ReportsQueued from "./ReportsQueued";
import "../styles/reports.css";

// Admin › Warehouse › Reports. GET /reports is the catalogue (already
// narrowed to what the caller may run); pick a warehouse and a report, add
// the dates when the report takes a range (or the sort day and batch when
// it takes "sort_batch"), and Download streams the file from
// GET /reports/:id/download. Reports are added on the BE
// (app/warehouse/services/reports.js) plus their two strings here:
// report_<id with - as _>_title / _desc. Until those exist the BE's English
// title and description show.

export interface ReportDef {
    id: string;
    category: string;
    title: string;
    description: string;
    params: string[];
}

// MCL PO Export trailers; must match MCL_TRAILER_LOCATIONS on the BE.
const MCL_TRAILERS = ["1", "2", "3"];

const readUser = () => {
    try {
        const raw = window.localStorage.getItem("user");
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

// yyyy-mm-dd in the browser's own day, `offset` days from today.
const localDay = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const today = () => localDay(0);
const yesterday = () => localDay(-1);

// Pseudo-warehouses the BE accepts by name (app/warehouse/services/
// warehouse-groups.js): a report run for UK reads Pembroke and Newport
// together, one file. Offered after the real warehouses, only to people who
// may cross warehouses, and only when every member is a known warehouse.
const WAREHOUSE_GROUPS: Record<string, string[]> = { UK: ["Pembroke", "Newport"] };

const ReportsAdmin = () => {
    const { t } = useTranslation();
    const warehouseScope = useWarehouseScope();
    const user = useMemo(readUser, []);

    const [warehouses, setWarehouses] = useState<string[]>([]);
    const [warehouse, setWarehouse] = useState<string>(user.warehouse || "");
    const [reports, setReports] = useState<ReportDef[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [selectedId, setSelectedId] = useState<string>("");
    // Date-range reports default to yesterday and today.
    const [from, setFrom] = useState(yesterday);
    const [to, setTo] = useState(today);
    // MCL PO Export: which trailer location to read (n → MCL_TRAILER_n).
    const [trailer, setTrailer] = useState<string>("1");
    const [downloading, setDownloading] = useState(false);
    // Sort-batch reports: the sort day (default today) and one of the
    // batches GET /reports/sort-batches finds on it.
    const [sortDay, setSortDay] = useState(today);
    const [batches, setBatches] = useState<number[] | null>(null);
    const [batch, setBatch] = useState("");

    // A translation when the FE has one, else the BE's English text.
    const tr = (key: string, fallback: string) => {
        const s = t(key);
        return s === key ? fallback : s;
    };
    const keyOf = (id: string) => `report_${id.replace(/-/g, "_")}`;
    const titleOf = (r: ReportDef) => tr(`${keyOf(r.id)}_title`, r.title);
    const descOf = (r: ReportDef) => tr(`${keyOf(r.id)}_desc`, r.description);

    // Without cross_warehouse the page stays on the person's own warehouse.
    useEffect(() => {
        if (!warehouseScope.canCross && warehouseScope.own && warehouse !== warehouseScope.own) setWarehouse(warehouseScope.own);
    }, [warehouseScope.canCross, warehouseScope.own, warehouse]);

    // Warehouse list from /settings, like the other warehouse pages.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                const list = data.map(r => r.warehouse).sort();
                setWarehouses(list);
                setWarehouse(w => (w && list.includes(w) ? w : list[0] ?? ""));
            } catch { /* leave empty */ }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/reports`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                setReports(Array.isArray(data?.reports) ? data.reports : []);
            } catch {
                toast.error(t("reports_load_failed"), { toastId: "reports-load" });
            } finally {
                setLoaded(true);
            }
        })();
    }, [t]);

    const selected = reports.find(r => r.id === selectedId) ?? null;
    const needsDates = !!selected?.params.includes("date_range");
    const needsTrailer = !!selected?.params.includes("mcl_trailer");
    const datesValid = !needsDates || (!!from && !!to && from <= to);
    const needsSortBatch = !!selected?.params.includes("sort_batch");
    const sortBatchValid = !needsSortBatch || (!!sortDay && !!batch);
    const canDownload = !!selected && !!warehouse && datesValid && sortBatchValid && !downloading;

    // The batches on the sort day, reloaded when the warehouse or day
    // changes. Keeps the picked batch when the day still has it, else the
    // latest one.
    useEffect(() => {
        if (!needsSortBatch || !warehouse || !sortDay) return;
        let alive = true;
        setBatches(null);
        (async () => {
            try {
                const p = new URLSearchParams({ warehouse, day: sortDay });
                const res = await apiFetch(`/reports/sort-batches?${p}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const list: number[] = Array.isArray(data?.batches) ? data.batches.map(Number) : [];
                if (!alive) return;
                setBatches(list);
                setBatch(b => (list.includes(Number(b)) ? b : list.length ? String(list[list.length - 1]) : ""));
            } catch {
                if (alive) { setBatches([]); setBatch(""); }
                toast.error(t("reports_batches_load_failed"), { toastId: "reports-batches" });
            }
        })();
        return () => { alive = false; };
    }, [needsSortBatch, warehouse, sortDay, t]);

    // Reports grouped by category, in catalogue order.
    const groups = useMemo(() => {
        const out: { category: string; items: ReportDef[] }[] = [];
        for (const r of reports) {
            const g = out.find(x => x.category === r.category);
            if (g) g.items.push(r);
            else out.push({ category: r.category, items: [r] });
        }
        return out;
    }, [reports]);

    const warehouseOptions = [
        ...warehouseScope.limit(warehouses),
        ...(warehouseScope.canCross
            ? Object.entries(WAREHOUSE_GROUPS).filter(([, members]) => members.every(m => warehouses.includes(m))).map(([name]) => name)
            : []),
    ].map(w => ({ value: w, label: w }));

    const download = async () => {
        if (!selected || !canDownload) return;
        setDownloading(true);
        try {
            const p = new URLSearchParams({ warehouse });
            if (needsDates) { p.set("from", from); p.set("to", to); }
            if (needsSortBatch) { p.set("day", sortDay); p.set("batch", batch); }
            if (needsTrailer) p.set("trailer", trailer);
            const res = await apiFetch(`/reports/${encodeURIComponent(selected.id)}/download?${p}`, {});
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.message || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            // The BE names the file; fall back to id-warehouse-date with the type's extension.
            const disposition = res.headers.get("Content-Disposition") ?? "";
            const named = /filename="([^"]+)"/.exec(disposition)?.[1];
            const ext = /text\/csv/i.test(res.headers.get("Content-Type") ?? "") ? "csv" : "xlsx";
            const filename = named || `${selected.id}-${warehouse}-${today()}.${ext}`.replace(/[^\w.-]/g, "_");
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(`${t("reports_download_failed")}${e instanceof Error && e.message ? ` (${e.message})` : ""}`, { toastId: "reports-download" });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="reports-admin">
            <FilterPills
                className="warehouse-admin-pills"
                value={warehouse}
                onChange={setWarehouse}
                options={warehouseOptions}
                hideAll
                labelKey="warehouse_col"
            />

            {loaded && reports.length === 0 && (
                <p className="reports-empty">{t("reports_none")}</p>
            )}

            {groups.map(g => (
                <section key={g.category} className="reports-group">
                    <h3 className="reports-group-title">{tr(`reports_category_${g.category}`, g.category)}</h3>
                    <div className="reports-list" role="radiogroup" aria-label={tr(`reports_category_${g.category}`, g.category)}>
                        {g.items.map(r => (
                            <button
                                key={r.id}
                                type="button"
                                role="radio"
                                aria-checked={r.id === selectedId}
                                className={`reports-item${r.id === selectedId ? " reports-item--selected" : ""}`}
                                aria-labelledby={`reports-title-${r.id}`}
                                aria-describedby={`reports-desc-${r.id}`}
                                onClick={() => setSelectedId(r.id)}
                            >
                                <FileSpreadsheet size={20} strokeWidth={1.6} className="reports-item-icon" />
                                <span id={`reports-title-${r.id}`} className="reports-item-title">{titleOf(r)}</span>
                                {/* The description lives behind an info icon: shown on its hover,
                                    and whenever the item has keyboard focus. Screen readers get it
                                    through aria-describedby whether or not it is visible. */}
                                <span className="reports-item-info" aria-hidden="true">
                                    <Info size={16} strokeWidth={1.8} />
                                </span>
                                <span id={`reports-desc-${r.id}`} role="tooltip" className="reports-item-desc">{descOf(r)}</span>
                            </button>
                        ))}
                    </div>
                </section>
            ))}

            {reports.length > 0 && (
                <div className="reports-actions">
                    {needsDates && (
                        <>
                            <label className="reports-date">
                                {t("reports_from")}
                                <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
                            </label>
                            <label className="reports-date">
                                {t("reports_to")}
                                <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
                            </label>
                        </>
                    )}
                    {needsSortBatch && (
                        <>
                            <label className="reports-date">
                                {t("reports_sort_day")}
                                <input type="date" value={sortDay} onChange={e => setSortDay(e.target.value)} />
                            </label>
                            <label className="reports-date">
                                {t("reports_batch")}
                                <select
                                    value={batch}
                                    onChange={e => setBatch(e.target.value)}
                                    disabled={!batches || batches.length === 0}
                                >
                                    {batches === null && <option value="">{t("reports_batches_loading")}</option>}
                                    {batches?.length === 0 && <option value="">{t("reports_batches_none")}</option>}
                                    {batches?.map(b => <option key={b} value={String(b)}>{t("reports_batch_n", { batch: b })}</option>)}
                                </select>
                            </label>
                        </>
                    )}
                    {needsTrailer && (
                        <label className="reports-date">
                            {t("reports_trailer")}
                            <select value={trailer} onChange={e => setTrailer(e.target.value)}>
                                {MCL_TRAILERS.map(n => <option key={n} value={n}>{`MCL_TRAILER_${n}`}</option>)}
                            </select>
                        </label>
                    )}
                    <span className="reports-selected">
                        {selected ? `${titleOf(selected)} · ${warehouse}` : t("reports_pick_one")}
                    </span>
                    <button type="button" className="btn btn-primary" onClick={download} disabled={!canDownload}>
                        <Download size={16} />
                        {downloading ? t("reports_downloading") : t("reports_download")}
                    </button>
                </div>
            )}

            <ReportsQueued />
            {warehouse && <ReportsNightly warehouse={warehouse} />}
        </div>
    );
};

export default ReportsAdmin;
